import { z } from "zod";
import { BillingModeSchema, EventKindSchema } from "./common";
import { EventFeaturesSchema } from "./event-features";
import { EventProgressSchema } from "./event-progress";

export const RequestChannelSchema = z.enum([
  "phone",
  "line",
  "facebook",
  "walk_in",
  "referral",
  "other",
]);

export const RequestStatusSchema = z.enum([
  "new",
  "negotiating",
  "pending_quote",
  "won",
  "lost",
  "cancelled",
]);

export const RequestDateModeSchema = z.enum(["event_date", "show_dates"]);
export type RequestDateMode = z.infer<typeof RequestDateModeSchema>;

export const RequestShowDateSchema = z.object({
  id: z.string(),
  requestId: z.string(),
  showDate: z.coerce.date(),
  sortOrder: z.number(),
  notes: z.string().nullable().optional(),
});
export type RequestShowDateDTO = z.infer<typeof RequestShowDateSchema>;

export const RequestSchema = z.object({
  id: z.string(),
  title: z.string(),
  customerName: z.string().nullable(),
  contactName: z.string(),
  contactPhone: z.string().nullable(),
  contactEmail: z.string().nullable(),
  contactAddress: z.string().nullable(),
  channel: RequestChannelSchema,
  status: RequestStatusSchema,
  requestedEventDate: z.coerce.date().nullable(),
  categoryId: z.string().nullable(),
  dateMode: RequestDateModeSchema,
  showDates: z.array(RequestShowDateSchema).optional(),
  notes: z.string().nullable(),
  assignedTo: z.string().nullable(),
  assignedToName: z.string().nullable().optional(),
  priorityOrder: z.number().nullable(),
  convertedEventId: z.string().nullable(),
  convertedAt: z.coerce.date().nullable(),
  createdBy: z.string().nullable(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
  convertedEventTitle: z.string().nullable().optional(),
  /** Phase 22.1 — งานที่แปลงแล้วเคยเลื่อน */
  convertedEventWasPostponed: z.boolean().optional(),
  convertedEventDateTbd: z.boolean().optional(),
  convertedEventKind: EventKindSchema.nullable().optional(),
  convertedEventBillingMode: BillingModeSchema.nullable().optional(),
  /** Progress ของงานที่แปลงแล้ว (null เมื่อยังไม่แปลง) */
  convertedEventProgress: EventProgressSchema.nullable().optional(),
});
export type RequestDTO = z.infer<typeof RequestSchema>;

export const CreateRequestSchema = z
  .object({
    title: z.string().min(1, "ຕ້ອງປ້ອນຫົວຂໍ້"),
    customerName: z.string().nullable().optional(),
    contactName: z.string().min(1, "ຕ້ອງປ້ອນຊື່ຜູ້ຕິດຕໍ່"),
    contactPhone: z.string().nullable().optional(),
    contactEmail: z.string().email().nullable().optional().or(z.literal("")),
    contactAddress: z.string().nullable().optional(),
    channel: RequestChannelSchema.default("phone"),
    status: RequestStatusSchema.default("new"),
    requestedEventDate: z.string().nullable().optional(),
    categoryId: z.string().nullable().optional(),
    dateMode: RequestDateModeSchema.default("event_date"),
    showDates: z.array(z.string()).optional(),
    notes: z.string().nullable().optional(),
    assignedTo: z.string().nullable().optional(),
    priorityOrder: z.number().int().nullable().optional(),
  })
  .superRefine((vals, ctx) => {
    if (vals.dateMode === "show_dates") {
      const dates = (vals.showDates ?? []).filter((d) => d.trim());
      if (dates.length === 0) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["showDates"],
          message: "ຕ້ອງເລືອກຢ່າງໜ້ອຍ 1 ວັນໂຊ",
        });
      }
    }
  });
export type CreateRequestDTO = z.infer<typeof CreateRequestSchema>;

export const UpdateRequestSchema = z
  .object({
    title: z.string().min(1, "ຕ້ອງປ້ອນຫົວຂໍ້").optional(),
    customerName: z.string().nullable().optional(),
    contactName: z.string().min(1, "ຕ້ອງປ້ອນຊື່ຜູ້ຕິດຕໍ່").optional(),
    contactPhone: z.string().nullable().optional(),
    contactEmail: z.string().email().nullable().optional().or(z.literal("")),
    contactAddress: z.string().nullable().optional(),
    channel: RequestChannelSchema.optional(),
    status: RequestStatusSchema.optional(),
    requestedEventDate: z.string().nullable().optional(),
    categoryId: z.string().nullable().optional(),
    dateMode: RequestDateModeSchema.optional(),
    showDates: z.array(z.string()).optional(),
    notes: z.string().nullable().optional(),
    assignedTo: z.string().nullable().optional(),
    priorityOrder: z.number().int().nullable().optional(),
  })
  .superRefine((vals, ctx) => {
    if (vals.dateMode === "show_dates" && vals.showDates !== undefined) {
      const dates = vals.showDates.filter((d) => d.trim());
      if (dates.length === 0) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["showDates"],
          message: "ຕ້ອງເລືອກຢ່າງໜ້ອຍ 1 ວັນໂຊ",
        });
      }
    }
  });
export type UpdateRequestDTO = z.infer<typeof UpdateRequestSchema>;

export const ConvertRequestToEventSchema = z.object({
  title: z.string().min(1).optional(),
  status: z
    .enum(["draft", "confirmed", "completed", "cancelled"])
    .default("confirmed"),
  eventDate: z.string().min(1).optional(),
  endDate: z.string().nullable().optional(),
  durationMinutes: z.number().int().min(1).nullable().optional(),
  locationText: z.string().nullable().optional(),
  locationProvince: z.string().nullable().optional(),
  isProvincial: z.boolean().optional(),
  perDiemPolicyId: z.string().nullable().optional(),
  perDiemDays: z.number().int().min(1).nullable().optional(),
  categoryId: z.string().nullable().optional(),
  eventFeatures: EventFeaturesSchema.optional(),
  roomId: z.string().nullable().optional(),
  priorityOrder: z.number().int().nullable().optional(),
  notes: z.string().nullable().optional(),
  copyFromEventId: z.string().min(1).optional(),
});
export type ConvertRequestToEventDTO = z.infer<
  typeof ConvertRequestToEventSchema
>;
